/* eslint no-tabs: "off", indent: "off", no-case-declarations: "off", no-lonely-if: "off" */

import React from "react";
import _ from "underscore";
import moment from "moment";

import {DEF_GOOGLE_TRANSLATE_LANGUAGE_NAME, DEF_GOOGLE_TRANSLATE_LANGUAGE_ID} from "constants/constants.js";

import {
	ERROR_LOADING_TWEETS,
	FETCHING_POST_COMMENTS,
	FETCHING_TWEETS,
	NO_POST_COMMENTS,
	RECEIVE_POST_COMMENTS,
	RECEIVE_TWEETS,
	INIT_DOCUMENT_SUMMARIZATION,
	RECEIVE_DOCUMENT_SUMMARIZATION,
	RECEIVE_DOCUMENT_SUMMARIZATION_ERROR,
	RESET_DOCUMENT_SUMMARIZATION,
} from "actions/constants.js";

import { addColorToGraphs } from "lib/color.js";
import { organizeSocial } from "lib/helpers/expandedContentHelpers.js";
import { unintelligibleTitle } from "lib/helpers/textHelpers";

const initialState = {
	// Expanded Content Modal
	expandedContentModalLoading: false,
	expandedContentModalOpen: false,
	expandedContent: false, // data object for expanded content modal or false
	expandedContentId: "", // used to fetch the scraped content
	unsafeArticleData: false, // false or html string
	translatedContent: false, // false or html string
	translatedTitle: false, // false or html string
	translationError: false,
	selectedGoogleTranslateLang: {
		id: DEF_GOOGLE_TRANSLATE_LANGUAGE_ID, 
		name: DEF_GOOGLE_TRANSLATE_LANGUAGE_NAME, 
	},

	contentSummary: false,
	contentSummaryLoading: false,
	contentSummaryError: false,

	// Social feed
	socialFeed: [], // array of tweets and facebook comment for the social media tab
	facebookFeed: [], // storage to compile main feed out of, and to count number of comments
	twitterFeed: [], // storage to compile main feed out of, and to count number of tweets
	tweetsLoading: false,
	twitterFeedError: false,
	facebookLoading: false,
	facebookCommentsError: false,
	noSocial: true, // Set to true when the modal opens, in case there isn't a link to use to grab social data

	// Twitter modal
	tweetId: false,
	twitterModalLoading: false,
	twitterModalOpen: false,
	twitterModalFallback: {},

	// Map specific data
	mapDataLoading: false,
	currentMap: false, // data object for expanded content modal map or false
	baseMapObject: false, // data object for expanded content modal map or false - base only
	mapKey: [],
	mapTooltip: false, // don't use Tooltip component
	mapTooltipColor: "",
	mapTooltipTitle: "",
	mapTooltipValue: "",
	mapTooltipId: "",
	mapHighlight: false,
	mapHighlightColor: "",
	mapHighlightId: false,
	mapHighlightTitle: "",
	mapHighlightValue: "",

	// animation value
	expandedContentNoTransition: true,
	expandedContentModalY: 0,
	expandedContentModalScale: {
		height: 0,
		width: 0,
	},
	twitterNoTransition: true,
	twitterModalY: 0,
	twitterModalScale: {
		height: 0,
		width: 0,
	},
};

const expandedContent = (state = initialState, action) => {
	switch (action.type) {
		// Expanded content modal
		// fires imediately before "openContentModal"
		case "sizeContentModal":
			const modalScale = {
				height: action.offsetHeight / (action.windowSize.height - 300),
				width: action.offsetWidth / (action.windowSize.width - 300),
			};

			// Transition takes place with the scaling, which put the location in the center of the
			//		screen, less the width & height of the shrunk div.
			// 		modal starts (action.windowSize.height / 2) - 11 from the top (scaled modal is 22px high)

			// scaling isn't precise enough (on Chrome, at least) to mimic the div exaclty, so leaving
			// 		the vertical alignment in the center is fine.

			return Object.assign({}, state, {
				expandedContentNoTransition: true,
				expandedContentModalLoading: true,
				expandedContentModalY: action.offsetY - action.windowSize.height / 2 - 11,
				expandedContentModalScale: modalScale,
			});
		case "resetContentModal":
			return Object.assign({}, state, {
				expandedContentModalLoading: true,
				expandedContent: false,
				contentSummary: false,
				contentSummaryLoading: false,
				contentSummaryError: false,
			});
		case "sizeTwitterModal":
			const twitterModalScale = {
				height: action.offsetHeight / (action.windowSize.height - 300),
				width: action.offsetWidth / (action.windowSize.width - 300),
			};
			return Object.assign({}, state, {
				twitterNoTransition: true,
				twitterModalLoading: true,
				twitterModalY: action.offsetY - action.windowSize.height / 2 - 11,
				twitterModalScale,
			});
		case "openContentModal":
			return Object.assign({}, state, {
				expandedContentModalOpen: true,
				expandedContentNoTransition: false,
				facebookFeed: [],
				noSocial: true,
				socialFeed: [],
				twitterFeed: [],
				twitterFeedError: false,
				translatedContent: false,
				translatedTitle: false,
				translationError: false,
			});
		case "closeContentModal":
			return Object.assign({}, state, {
				expandedContentModalOpen: false,
				expandedContent: false,
				contentSummary: false,
				contentSummaryLoading: false,
				contentSummaryError: false,
				expandedContentMap: false,
				unsafeArticleData: false,
			});

		case "openTwitterModal":
			return Object.assign({}, state, {
				twitterModalOpen: true,
				twitterNoTransition: false,
			});
		case "closeTwitterModal":
			return Object.assign({}, state, {
				twitterModalOpen: false,
				tweetId: false,
			});
		case "receiveArticleData": {
			const { distractionFreeDeveloperMode } = action;
			if (distractionFreeDeveloperMode) {
				action.data.text = `${unintelligibleTitle(50)}<br/><br/>${unintelligibleTitle(40)}<br/><br/>${unintelligibleTitle(65)}`;
			}
			const expandedContentData = addColorToGraphs(action.data, action.narratives);

			return Object.assign({}, state, {
				expandedContent: expandedContentData,
				expandedContentModalLoading: false,
				expandedContentId: action.id,
				expandedContentIndex: action.index,
			});
		}
		case FETCHING_TWEETS:
			return Object.assign({}, state, {
				tweetsLoading: true,
				noSocial: false, // NOTE: this will need to be decoupled if we ever don't try to fetch tweets but still get other social content.
			});
		case RECEIVE_TWEETS: {
			const { tweets } = action;
			const { facebookFeed } = state;
			_.each(tweets, (tweet) => {
				tweet.date = Number(moment(tweet.time, "hh:mm A - DD MMM YYYY").format("x"));
			});
			return Object.assign({}, state, {
				socialFeed: organizeSocial({ facebook: facebookFeed, twitter: tweets }),
				twitterFeed: tweets,
				tweetsLoading: false,
			});
		}
		case ERROR_LOADING_TWEETS:
			return Object.assign({}, state, {
				twitterFeedError: true,
				tweetsLoading: false,
			});

		case FETCHING_POST_COMMENTS: {
			return Object.assign({}, state, {
				facebookLoading: true,
				facebookCommentsError: false,
			});
		}
		case NO_POST_COMMENTS: {
			// NOTE: differentiate between error types here
			// const { error } = action;
			// const forceNoComments = error === "PostNotFound";
			return Object.assign({}, state, {
				facebookLoading: false,
				facebookCommentsError: true,
			});
		}
		case RECEIVE_POST_COMMENTS: {
			const { comments } = action;
			// console.log("RECEIVE_POST_COMMENTS", comments);
			const { twitterFeed } = state;
			_.each(comments, (post) => {
				post.date = post.created_at * 1000;
			});
			return Object.assign({}, state, {
				socialFeed: organizeSocial({ facebook: comments, twitter: twitterFeed }),
				facebookFeed: JSON.parse(JSON.stringify(comments)),
				facebookCommentsError: false,
				facebookLoading: false,
			});
		}

		case INIT_DOCUMENT_SUMMARIZATION: {
			return {
				...state,
				contentSummary: false,
				contentSummaryLoading: true,
				contentSummaryError: false,
			};
		}

		case RECEIVE_DOCUMENT_SUMMARIZATION: {
			return {
				...state,
				contentSummary: action.data.text,
				contentSummaryLoading: false,
				contentSummaryError: false,
			};
		}

		case RECEIVE_DOCUMENT_SUMMARIZATION_ERROR: {
			return {
				...state,
				contentSummary: "",
				contentSummaryLoading: false,
				contentSummaryError: action.error,
			};
		}

		case RESET_DOCUMENT_SUMMARIZATION: {
			return {
				...state,
				contentSummary: false,
				contentSummaryLoading: false,
				contentSummaryError: false,
			};
		}

		case "receiveTranslation":
			// console.log("TRANSLATION", action.data, action.data[0][0]);
			let translateResult = "";
			const translatedByLine = action.data[0];
			translatedByLine.forEach((line, idx, array) => {
				if (line[0]) {
					translateResult += line[0];
					if (idx !== array.length-1) {
						translateResult += "\n"; 
					}
				}
			});
			return Object.assign({}, state, {
				translatedContent: action.textType === "content" ? translateResult : state.translatedContent,
				translatedTitle: action.textType === "title" ? translateResult : state.translatedTitle,
			});
			
		case "translationError":
			return Object.assign({}, state, {
				translationError: true,
			});
		case "receiveUnsafeArticle":
			return Object.assign({}, state, {
				unsafeArticleData: action.data && action.data.unsafe,
			});

		case "setTwitterCard": {
			const tweetId = action.content.tweetId || action.content.id;

			return Object.assign({}, state, {
				tweetId: tweetId && tweetId.toString(),
				twitterModalLoading: false,
				twitterModalFallback: action.content,
			});
		}
		case "receiveArticleMapData":
			// console.log("receiveMapData - stateCode", action.stateCode);
			const currentMap = {
				location: action.map.dataTotal.location,
				shares: action.map.dataTotal.shares,
				startDate: action.map.startDate,
				endDate: action.map.endDate,
			};
			return Object.assign({}, state, {
				mapDataLoading: false,
				mapKey: _.sortBy(JSON.parse(JSON.stringify(action.map.levels)), "level").reverse(),
				currentMap,
				baseMapObject: action.stateCode ? state.baseMapObject : currentMap,
			});
		case "hoverContentMap":
			return Object.assign({}, state, {
				mapTooltip: true,
				mapTooltipColor: action.color,
				mapTooltipId: action.data && action.data.geoState,
				mapTooltipTitle: action.data && action.data.name,
				mapTooltipValue:
					action.data && action.data.shares
						? `${Number(action.data.shares).toLocaleString()} shares`
						: action.data && action.data.spending
						? `${action.data.spending} spending`
						: null,
			});
		case "unhoverContentMap":
			return Object.assign({}, state, {
				mapTooltip: false,
			});
		case "selectGoogleLang":
			return Object.assign({}, state, {
				selectedGoogleTranslateLang: {name: action.name, id: action.id}
			});
		case "contentMapDataLoading":
			return Object.assign({}, state, {
				mapDataLoading: true, // flag falsified by receiveMapData
				mapHighlightStoredText: `${Number(action.topShares).toLocaleString()} shares`,
			});
		case "highlightContentMap":
			return Object.assign({}, state, {
				mapHighlight: true,
				mapHighlightId: action.data.geoState,
				mapHighlightColor: action.color,
				// mapHighlightStartDate: action.data.startDate,
				// mapHighlightEndDate: action.data.endDate,
				mapHighlightTitle: action.data.name,
				mapHighlightValue: action.data.shares
					? `${Number(action.data.shares).toLocaleString()} shares`
					: action.data.spending
					? `${action.data.spending} spending`
					: null,
				mapPlaying: false,
				mapPosition: 0,
			});

		case "clearContentMapHighlight":
			return Object.assign({}, state, {
				mapTooltip: false,
				mapHighlight: false,
				mapHighlightId: false,
				mapHighlightColor: "",
				mapHighlightTitle: "",
				mapHighlightValue: "",

				// reset the map to the base data
				mapDataLoading: false,
				currentMap: state.baseMapObject,
			});

		default:
			return state;
	}
};

export default expandedContent;
